import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Icons,
  Input,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Table,
  TextField,
  Tooltip,
} from '@pankod/refine-antd'
import { CrudFilters, useCreate, useInvalidate } from '@pankod/refine-core'
import { debounce } from 'lodash'
import React from 'react'

import {
  SelectCityOptions,
  SelectDistrictOptions,
  SelectProvinceOptions,
} from '../../../basic-ppg-management/lptk/blocks/SelectLPTKDataOptions'
import { kapabilitasMapper } from '@components/DataTableParticipants/blocks/utils'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import useTableWithMeta from '../../../../hooks/useTableWithMeta'
import { TCommonError } from '../../../../interfaces/common'

type TSindaraKapabilitas =
  | 'INSTRUKTUR'
  | 'ASISTEN'
  | 'ADMIN_KELAS'
  | 'PESERTA'
  | 'instruktur'
  | 'asisten'
  | 'admin_kelas'
  | 'peserta'

type TSindaraKapabilitasFilter =
  | 'instruktur'
  | 'asisten'
  | 'admin_kelas'
  | 'peserta'

type TSindaraParticipant = {
  userId: string
  email: string
  name: string
  participantType: string
  kapabilitas?: TSindaraKapabilitas
  jenjang: string
  provinsiCode: string
  provinsi: string
  kabupatenCode: string
  kabupaten: string
  kecamatanCode: string
  kecamatan: string
  kelompokKerja: string
  enrolledAt?: string
}

type TSindaraParticipantTableRecord = TSindaraParticipant & {
  tableRowKey: string
}

type TSindaraParticipantFilter = {
  searchMode?: 'name' | 'email'
  searchQuery?: string
  kapabilitas?: TSindaraKapabilitasFilter[]
  kelompokKerja?: string
  kodeProvinsi?: string
  kodeKabupaten?: string
  kodeKecamatan?: string
}

type TSindaraParticipantsTableProps = {
  programId?: string
  programLocalId?: string
  classId?: string
  instrukturCount?: number
  disableAssignment?: boolean
}

type TSindaraParticipantPayload = Pick<
  TSindaraParticipant,
  'email' | 'name' | 'userId'
>

export const SINDARA_KAPABILITAS_OPTIONS: {
  label: string
  value: TSindaraKapabilitasFilter
}[] = [
  { label: 'Fasilitator', value: 'instruktur' },
  { label: 'Asisten', value: 'asisten' },
  { label: 'Admin Kelas', value: 'admin_kelas' },
  { label: 'Peserta', value: 'peserta' },
]

const MAX_SINDARA_INSTRUKTUR_PER_CLASS = 2
const SINDARA_INSTRUKTUR_LIMIT_MESSAGE = 'Maksimal 2 fasilitator per kelas'

const normalizeKapabilitas = (
  value?: TSindaraKapabilitas,
): TSindaraKapabilitasFilter =>
  value?.toLowerCase() === 'instruktur' ||
  value?.toLowerCase() === 'asisten' ||
  value?.toLowerCase() === 'admin_kelas'
    ? (value.toLowerCase() as TSindaraKapabilitasFilter)
    : 'peserta'

const formatKapabilitas = (value?: string) =>
  value ? kapabilitasMapper(value.toLowerCase()) : '-'

const isSindaraPeserta = (record: TSindaraParticipant) =>
  normalizeKapabilitas(record.kapabilitas) === 'peserta'

const isSindaraInstruktur = (record: TSindaraParticipant) =>
  normalizeKapabilitas(record.kapabilitas) === 'instruktur'

const isSindaraParticipantBlockedByEnrollment = (record: TSindaraParticipant) =>
  isSindaraPeserta(record) && Boolean(record.enrolledAt)

const getRemainingInstrukturSlots = (instrukturCount = 0) =>
  Math.max(MAX_SINDARA_INSTRUKTUR_PER_CLASS - instrukturCount, 0)

const getSindaraParticipantRowKey = (
  record: TSindaraParticipant,
  index?: number,
) =>
  [record.userId, record.email, record.name, `row-${index ?? 'unknown'}`]
    .map((value) => value || `empty-${index ?? 'unknown'}`)
    .join('-')

const getSindaraParticipantRowClassName = (record: TSindaraParticipant) =>
  isSindaraParticipantBlockedByEnrollment(record)
    ? 'sindara-participant-row-disabled'
    : ''

const mapSindaraParticipantPayload = (
  participants: TSindaraParticipantPayload[],
) =>
  participants.map(({ email, name, userId }) => ({
    email,
    name,
    userId,
  }))

const appendFilterWhenPresent = (
  filters: CrudFilters,
  field: string,
  value?: string | TSindaraKapabilitasFilter[],
) => {
  if (Array.isArray(value)) {
    if (value.length) {
      filters.push({
        field,
        operator: 'eq',
        value,
      })
    }

    return
  }

  const trimmedValue = value?.trim()

  if (trimmedValue) {
    filters.push({
      field,
      operator: 'eq',
      value: trimmedValue,
    })
  }
}

const buildSindaraParticipantFilters = ({
  searchMode = 'name',
  searchQuery,
  kapabilitas,
  kelompokKerja,
  kodeProvinsi,
  kodeKabupaten,
  kodeKecamatan,
}: TSindaraParticipantFilter): CrudFilters => {
  const filters: CrudFilters = []

  appendFilterWhenPresent(filters, searchMode, searchQuery)
  appendFilterWhenPresent(filters, 'kelompok_kerja', kelompokKerja)
  appendFilterWhenPresent(filters, 'kodeProvinsi', kodeProvinsi)
  appendFilterWhenPresent(filters, 'kodeKabupaten', kodeKabupaten)
  appendFilterWhenPresent(filters, 'kodeKecamatan', kodeKecamatan)
  appendFilterWhenPresent(filters, 'kapabilitas', kapabilitas)

  return filters
}

const SindaraParticipantsTable = ({
  programId,
  programLocalId,
  classId,
  instrukturCount = 0,
  disableAssignment = false,
}: TSindaraParticipantsTableProps) => {
  const [searchMode, setSearchMode] =
    React.useState<TSindaraParticipantFilter['searchMode']>('name')
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])
  const [selectedParticipants, setSelectedParticipants] = React.useState<
    TSindaraParticipantTableRecord[]
  >([])
  const [showAssignModal, setShowAssignModal] = React.useState(false)

  const { tableProps, searchFormProps } = useTableWithMeta<
    TSindaraParticipantTableRecord,
    TCommonError,
    TSindaraParticipantFilter
  >({
    resource: 'sindara/participants',
    dataProviderName: 'lms',
    defaultSetFilterBehavior: 'replace',
    metaData: {
      queryArrayFormat: 'none',
    },
    onSearch: buildSindaraParticipantFilters,
  })
  const filterKodeProvinsi = Form.useWatch('kodeProvinsi', searchFormProps.form)
  const filterKodeKabupaten = Form.useWatch(
    'kodeKabupaten',
    searchFormProps.form,
  )
  const { mutateAsync: assignSindaraParticipants, isLoading: isAssigning } =
    useCreate()
  const invalidate = useInvalidate()
  const shouldDisableAssignment =
    disableAssignment || !programId || !programLocalId || !classId
  const remainingInstrukturSlots = getRemainingInstrukturSlots(instrukturCount)
  const selectedInstrukturCount =
    selectedParticipants.filter(isSindaraInstruktur).length

  const tableDataSource = React.useMemo(
    () =>
      tableProps.dataSource?.map((record, index) => ({
        ...record,
        tableRowKey: getSindaraParticipantRowKey(record, index),
      })),
    [tableProps.dataSource],
  )

  const resetAssignmentState = React.useCallback(() => {
    setSelectedParticipants([])
    setSelectedRowKeys([])
    setShowAssignModal(false)
  }, [])

  const getAssignableParticipants = React.useCallback(
    (participants: TSindaraParticipantTableRecord[]) => {
      let instrukturSlots = remainingInstrukturSlots

      return participants.filter((participant) => {
        if (isSindaraParticipantBlockedByEnrollment(participant)) {
          return false
        }

        if (!isSindaraInstruktur(participant)) {
          return true
        }

        if (instrukturSlots <= 0) {
          return false
        }

        instrukturSlots -= 1

        return true
      })
    },
    [remainingInstrukturSlots],
  )

  const handleOpenAssignModal = React.useCallback(
    (participants: TSindaraParticipantTableRecord[]) => {
      const assignableParticipants = getAssignableParticipants(participants)

      if (!assignableParticipants.length) {
        return
      }

      setSelectedParticipants(assignableParticipants)
      setSelectedRowKeys(
        assignableParticipants.map((participant) => participant.tableRowKey),
      )
      setShowAssignModal(true)
    },
    [getAssignableParticipants],
  )

  const handleSubmitAssignment = React.useCallback(async () => {
    if (
      shouldDisableAssignment ||
      !programId ||
      !programLocalId ||
      !classId ||
      selectedParticipants.length === 0
    ) {
      return
    }

    await assignSindaraParticipants(
      {
        resource: `programs/${programId}/local-programs/${programLocalId}/classes/${classId}/sindara/participants`,
        dataProviderName: 'lms',
        values: {
          users: mapSindaraParticipantPayload(selectedParticipants),
        },
        successNotification: {
          message: 'Partisipan Sindara berhasil ditambahkan ke kelas',
          description: 'Sukses',
          type: 'success',
        },
        errorNotification: (error) =>
          showErrorNotification(
            error,
            'Terdapat gangguan saat menambahkan partisipan Sindara ke kelas',
          ),
      },
      {
        onSuccess: () => {
          resetAssignmentState()
          invalidate({
            invalidates: ['list'],
            resource: 'sindara/participants',
            dataProviderName: 'lms',
          })
          invalidate({
            invalidates: ['detail'],
            resource: `programs/${programId}/local-programs/${programLocalId}/classes`,
            id: classId,
            dataProviderName: 'lms',
          })
        },
      },
    )
  }, [
    assignSindaraParticipants,
    classId,
    invalidate,
    programId,
    programLocalId,
    resetAssignmentState,
    selectedParticipants,
    shouldDisableAssignment,
  ])

  const submitSearch = React.useMemo(
    () =>
      debounce(() => {
        searchFormProps.form?.submit()
      }, 500),
    [searchFormProps.form],
  )

  React.useEffect(() => {
    searchFormProps.form?.setFieldsValue({ searchMode })
    submitSearch()
  }, [searchFormProps.form, searchMode, submitSearch])

  React.useEffect(() => {
    return () => {
      submitSearch.cancel()
    }
  }, [submitSearch])

  return (
    <div>
      <Form
        {...searchFormProps}
        autoComplete="off"
        layout="vertical"
        onValuesChange={() => submitSearch()}
      >
        <Form.Item name="searchMode" hidden initialValue="name">
          <Input type="hidden" />
        </Form.Item>
        <p className="text-xs font-bold mb-2">Cari Peserta</p>
        <Form.Item name="searchQuery" className="w-[360px]">
          <Input
            addonBefore={
              <Select
                value={searchMode}
                onChange={(value) => {
                  setSearchMode(value)
                }}
              >
                <Select.Option value="name">Nama</Select.Option>
                <Select.Option value="email">Email</Select.Option>
              </Select>
            }
            placeholder={`Cari berdasarkan ${
              searchMode === 'email' ? 'email' : 'nama'
            }`}
            prefix={<Icons.SearchOutlined className="mr-2" />}
            autoComplete="off"
            allowClear
          />
        </Form.Item>

        <p className="text-xs font-bold mb-2">Filter Wilayah</p>
        <Row gutter={8}>
          <Col span={8}>
            <Form.Item name="kodeProvinsi">
              <SelectProvinceOptions
                autoComplete="off"
                aria-autocomplete="none"
                showSearch
                visible
                allowClear
                onChange={() => {
                  searchFormProps.form?.setFieldsValue({
                    kodeKabupaten: undefined,
                    kodeKecamatan: undefined,
                  })
                }}
                placeholder="Pilih Provinsi"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="kodeKabupaten">
              <SelectCityOptions
                autoComplete="off"
                aria-autocomplete="none"
                showSearch
                provinceId={filterKodeProvinsi}
                disabled={!filterKodeProvinsi}
                allowClear
                onChange={() => {
                  searchFormProps.form?.setFieldsValue({
                    kodeKecamatan: undefined,
                  })
                }}
                placeholder="Pilih Kota/Kabupaten"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="kodeKecamatan">
              <SelectDistrictOptions
                autoComplete="off"
                aria-autocomplete="none"
                showSearch
                kotaKabId={filterKodeKabupaten}
                disabled={!filterKodeKabupaten}
                allowClear
                placeholder="Pilih Kecamatan"
              />
            </Form.Item>
          </Col>
        </Row>

        <p className="text-xs font-bold mb-2">Filter Kelompok Kerja</p>
        <Form.Item name="kelompokKerja">
          <Input
            placeholder="Cari berdasarkan kelompok kerja"
            autoComplete="off"
            allowClear
          />
        </Form.Item>

        <Divider style={{ marginTop: 0 }} />

        <div className="flex flex-row">
          <div className="w-[180px] mr-4">
            <p className="text-xs font-bold mt-4 mb-2">Kapabilitas</p>
            <Form.Item name="kapabilitas">
              <Checkbox.Group>
                {SINDARA_KAPABILITAS_OPTIONS.map((item) => (
                  <Checkbox
                    key={item.value}
                    value={item.value}
                    style={{
                      marginLeft: '0px',
                      marginBottom: 8,
                      display: 'flex',
                    }}
                  >
                    {item.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </div>

          <div className="flex-1 overflow-x-auto">
            <Table<TSindaraParticipantTableRecord>
              {...tableProps}
              dataSource={tableDataSource}
              size="small"
              rowKey="tableRowKey"
              rowClassName={getSindaraParticipantRowClassName}
              rowSelection={
                shouldDisableAssignment
                  ? undefined
                  : {
                      selectedRowKeys,
                      onChange: (
                        _selectedKeys: React.Key[],
                        selectedRows: TSindaraParticipantTableRecord[],
                      ) => {
                        const assignableRows =
                          getAssignableParticipants(selectedRows)

                        setSelectedRowKeys(
                          assignableRows.map(
                            (participant) => participant.tableRowKey,
                          ),
                        )
                        setSelectedParticipants(assignableRows)
                      },
                      getCheckboxProps: (
                        record: TSindaraParticipantTableRecord,
                      ) => {
                        const isSelected = selectedRowKeys.includes(
                          record.tableRowKey,
                        )
                        const hasSelectedRemainingInstrukturSlot =
                          !isSindaraInstruktur(record) ||
                          isSelected ||
                          selectedInstrukturCount < remainingInstrukturSlots

                        return {
                          disabled:
                            isSindaraParticipantBlockedByEnrollment(record) ||
                            !hasSelectedRemainingInstrukturSlot,
                        }
                      },
                    }
              }
              scroll={{ y: 600, x: 1400 }}
              footer={
                !shouldDisableAssignment && selectedParticipants.length > 0
                  ? () => (
                      <Button
                        type="primary"
                        icon={<Icons.UserAddOutlined />}
                        onClick={() =>
                          handleOpenAssignModal(selectedParticipants)
                        }
                      >
                        Assign Selected Participants (
                        {selectedParticipants.length})
                      </Button>
                    )
                  : undefined
              }
            >
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="userId"
                title="User ID"
                width={140}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="email"
                title="E-mail"
                width={220}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="name"
                title="Nama"
                width={180}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="participantType"
                title="Tipe"
                width={130}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="jenjang"
                title="Jenjang"
                width={110}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="provinsi"
                title="Provinsi"
                width={180}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="kabupaten"
                title="Kabupaten"
                width={200}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="kecamatan"
                title="Kecamatan"
                width={180}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="kapabilitas"
                title="Kapabilitas"
                width={150}
                render={(value) => (
                  <TextField value={formatKapabilitas(value)} />
                )}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                dataIndex="kelompokKerja"
                title="Kelompok Kerja"
                width={180}
              />
              <Table.Column<TSindaraParticipantTableRecord>
                title="Aksi"
                width={200}
                fixed="right"
                render={(_, record) => {
                  if (isSindaraParticipantBlockedByEnrollment(record)) {
                    const enrolledStatusText = `Sudah dikelaskan di ${record.enrolledAt}`

                    return (
                      <Popover
                        content={
                          <span className="block max-w-[360px] break-words">
                            {enrolledStatusText}
                          </span>
                        }
                        trigger="hover"
                      >
                        <span
                          aria-label={enrolledStatusText}
                          className="inline-flex max-w-full min-w-0 items-start"
                        >
                          <Icons.CheckCircleOutlined className="mr-2 mt-1 flex-shrink-0" />
                          <span className="sindara-participant-status-text min-w-0">
                            {enrolledStatusText}
                          </span>
                        </span>
                      </Popover>
                    )
                  }

                  const isInstrukturLimitReached =
                    isSindaraInstruktur(record) && remainingInstrukturSlots <= 0
                  const assignButton = (
                    <Button
                      type="primary"
                      icon={<Icons.UserAddOutlined />}
                      disabled={
                        shouldDisableAssignment || isInstrukturLimitReached
                      }
                      onClick={() => handleOpenAssignModal([record])}
                      block
                    >
                      Assign
                    </Button>
                  )

                  if (isInstrukturLimitReached) {
                    return (
                      <Tooltip title={SINDARA_INSTRUKTUR_LIMIT_MESSAGE}>
                        <span>{assignButton}</span>
                      </Tooltip>
                    )
                  }

                  return assignButton
                }}
              />
            </Table>
          </div>
        </div>
      </Form>
      <Modal
        title="Konfirmasi Penambahan Partisipan Sindara"
        visible={showAssignModal}
        onCancel={resetAssignmentState}
        destroyOnClose
        width={700}
        footer={
          <Space>
            <Button onClick={resetAssignmentState}>Batal</Button>
            <Button
              type="primary"
              loading={isAssigning}
              disabled={selectedParticipants.length === 0}
              onClick={handleSubmitAssignment}
            >
              Assign Partisipan
            </Button>
          </Space>
        }
      >
        <Table<TSindaraParticipantTableRecord>
          dataSource={selectedParticipants}
          rowKey="tableRowKey"
          pagination={false}
          size="small"
          bordered
        >
          <Table.Column<TSindaraParticipantTableRecord>
            title="User ID"
            dataIndex="userId"
          />
          <Table.Column<TSindaraParticipantTableRecord>
            title="Partisipan"
            dataIndex="name"
            render={(value, record) => (
              <div className="flex flex-col gap-0">
                <TextField value={value} />
                <TextField value={record.email} type="secondary" />
              </div>
            )}
          />
          <Table.Column<TSindaraParticipantTableRecord>
            title="Kapabilitas"
            dataIndex="kapabilitas"
            width={150}
            render={(value) => <TextField value={formatKapabilitas(value)} />}
          />
        </Table>
      </Modal>
    </div>
  )
}

export {
  buildSindaraParticipantFilters,
  formatKapabilitas,
  getSindaraParticipantRowClassName,
  getSindaraParticipantRowKey,
  getRemainingInstrukturSlots,
  isSindaraParticipantBlockedByEnrollment,
  mapSindaraParticipantPayload,
  normalizeKapabilitas,
  SindaraParticipantsTable,
  type TSindaraParticipant,
  type TSindaraParticipantFilter,
}

export default SindaraParticipantsTable
